import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

const http = axios.create({ baseURL: API });
// FastAPI 422s carry detail as a list of {msg}; flatten so every toast shows a readable sentence
http.interceptors.response.use(undefined, (err) => {
  const d = err?.response?.data?.detail;
  if (Array.isArray(d)) err.response.data.detail = d.map((x) => x?.msg || String(x)).join("; ");
  else if (d && typeof d === "object") err.response.data.detail = d.message || JSON.stringify(d);
  return Promise.reject(err);
});

export const assetUrl = (mediaId, kind) => `${API}/media/${mediaId}/asset/${kind}`;

export const api = {
  // projects
  listProjects: () => http.get("/projects").then((r) => r.data),
  getProject: (id) => http.get(`/projects/${id}`).then((r) => r.data),
  createProject: (body) => http.post("/projects", body).then((r) => r.data),
  updateProject: (id, body) => http.put(`/projects/${id}`, body).then((r) => r.data),
  deleteProject: (id, purge = false) => http.delete(`/projects/${id}?purge=${purge}`).then((r) => r.data),
  dashboard: (id) => http.get(`/projects/${id}/dashboard`).then((r) => r.data),

  // media
  importMedia: (id, formData, onUploadProgress) =>
    http.post(`/projects/${id}/media/import`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
    }).then((r) => r.data),
  listMedia: (id, params = {}) => http.get(`/projects/${id}/media`, { params }).then((r) => r.data),
  getMedia: (mid) => http.get(`/media/${mid}`).then((r) => r.data),
  setQuality: (mid, quality_category) => http.patch(`/media/${mid}/quality`, { quality_category }).then((r) => r.data),
  deleteMedia: (mid) => http.delete(`/media/${mid}`).then((r) => r.data),
  aiAnalyze: (mid) => http.post(`/media/${mid}/ai-analyze`).then((r) => r.data),

  // jobs
  listJobs: (id, params = {}) => http.get(`/projects/${id}/jobs`, { params }).then((r) => r.data),
  jobsSummary: (id) => http.get(`/projects/${id}/jobs/summary`).then((r) => r.data),
  pauseJob: (jid) => http.post(`/jobs/${jid}/pause`).then((r) => r.data),
  resumeJob: (jid) => http.post(`/jobs/${jid}/resume`).then((r) => r.data),
  cancelJob: (jid) => http.post(`/jobs/${jid}/cancel`).then((r) => r.data),
  retryJob: (jid) => http.post(`/jobs/${jid}/retry`).then((r) => r.data),
  retryFailed: (id) => http.post(`/projects/${id}/jobs/retry-failed`).then((r) => r.data),

  // logs
  logs: (id, params = {}) => http.get(`/projects/${id}/logs`, { params }).then((r) => r.data),
  clearLogs: (id) => http.delete(`/projects/${id}/logs`).then((r) => r.data),

  // storage & system
  storage: (id) => http.get(`/projects/${id}/storage`).then((r) => r.data),
  system: () => http.get(`/system/status`).then((r) => r.data),

  // ai
  aiProviders: () => http.get(`/ai/providers`).then((r) => r.data),
  aiHealth: (name) => http.post(`/ai/providers/${name}/health`).then((r) => r.data),
  aiUpdate: (name, body) => http.put(`/ai/providers/${name}`, body).then((r) => r.data),

  // phase 2 — analysis & sync
  analyzeProject: (id, force = false) => http.post(`/projects/${id}/analyze?force=${force}`).then((r) => r.data),
  syncProject: (id) => http.post(`/projects/${id}/sync`).then((r) => r.data),
  groupSimilar: (id) => http.post(`/projects/${id}/group-similar`).then((r) => r.data),
  getSync: (id) => http.get(`/projects/${id}/sync`).then((r) => r.data),
  similarityGroups: (id) => http.get(`/projects/${id}/similarity-groups`).then((r) => r.data),
  analysisSummary: (id) => http.get(`/projects/${id}/analysis-summary`).then((r) => r.data),
  scenes: (id, media_id) => http.get(`/projects/${id}/scenes`, { params: media_id ? { media_id } : {} }).then((r) => r.data),

  // phase 3 — wedding intelligence
  eventsCatalog: () => http.get(`/wedding/events-catalog`).then((r) => r.data),
  analyzeWedding: (id, force = false) => http.post(`/projects/${id}/analyze/wedding?force=${force}`).then((r) => r.data),
  clusterFaces: (id) => http.post(`/projects/${id}/cluster-faces`).then((r) => r.data),
  transcript: (mid) => http.get(`/media/${mid}/transcript`).then((r) => r.data),
  mediaFaces: (mid) => http.get(`/media/${mid}/faces`).then((r) => r.data),
  persons: (id) => http.get(`/projects/${id}/persons`).then((r) => r.data),
  updatePerson: (pid, body) => http.patch(`/persons/${pid}`, body).then((r) => r.data),
  mergePersons: (id, source_id, target_id) => http.post(`/projects/${id}/persons/merge`, { source_id, target_id }).then((r) => r.data),
  updateTags: (mid, body) => http.patch(`/media/${mid}/tags`, body).then((r) => r.data),
  events: (id) => http.get(`/projects/${id}/events`).then((r) => r.data),
  search: (id, params) => http.get(`/projects/${id}/search`, { params }).then((r) => r.data),

  // phase 4 — references & style
  references: (id) => http.get(`/projects/${id}/references`).then((r) => r.data),
  addLocalReference: (id, fd) => http.post(`/projects/${id}/references/local`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  addYoutubeReference: (id, url, name) => http.post(`/projects/${id}/references/youtube`, { url, name }).then((r) => r.data),
  deleteReference: (rid) => http.delete(`/references/${rid}`).then((r) => r.data),
  music: (id) => http.get(`/projects/${id}/music`).then((r) => r.data),

  // phase 5 — story & edit plan
  generateStory: (id) => http.post(`/projects/${id}/story/generate`).then((r) => r.data),
  getStory: (id) => http.get(`/projects/${id}/story`).then((r) => r.data),
  generateEditPlan: (id) => http.post(`/projects/${id}/edit-plan/generate`).then((r) => r.data),
  getEditPlan: (id) => http.get(`/projects/${id}/edit-plan`).then((r) => r.data),
  regenScene: (id, sid) => http.post(`/projects/${id}/edit-plan/scene/${sid}/regenerate`).then((r) => r.data),
  lockScene: (id, sid, locked) => http.post(`/projects/${id}/edit-plan/scene/${sid}/lock`, { locked }).then((r) => r.data),
  setSceneMusic: (id, sid, media_id) => http.post(`/projects/${id}/edit-plan/scene/${sid}/music`, { media_id }).then((r) => r.data),
  sceneCommand: (id, sid, command) => http.post(`/projects/${id}/edit-plan/scene/${sid}/command`, { command }).then((r) => r.data),

  // phase 6/7 — render & export
  startRender: (id, settings) => http.post(`/projects/${id}/render`, settings || {}).then((r) => r.data),
  renderStatus: (id) => http.get(`/projects/${id}/render`).then((r) => r.data),
  startExport: (id, kind) => http.post(`/projects/${id}/export`, { kind }).then((r) => r.data),
  exports: (id) => http.get(`/projects/${id}/exports`).then((r) => r.data),
  getAudioMix: (id) => http.get(`/projects/${id}/audio-mix`).then((r) => r.data),
  setAudioMix: (id, body) => http.put(`/projects/${id}/audio-mix`, body).then((r) => r.data),

  // phase 6 — color & enhancement
  colorPresets: () => http.get(`/color/presets`).then((r) => r.data),
  getColor: (id) => http.get(`/projects/${id}/color`).then((r) => r.data),
  analyzeColor: (id, force = false) => http.post(`/projects/${id}/color/analyze?force=${force}`).then((r) => r.data),
  updateColor: (id, body) => http.put(`/projects/${id}/color`, body).then((r) => r.data),
  tweakCamera: (id, cid, body) => http.put(`/projects/${id}/color/camera/${cid}`, body).then((r) => r.data),
  getEnhancement: (id) => http.get(`/projects/${id}/enhancement`).then((r) => r.data),
  setEnhancement: (id, body) => http.put(`/projects/${id}/enhancement/settings`, body).then((r) => r.data),
  runEnhancement: (id, media_ids) => http.post(`/projects/${id}/enhancement/run`, { media_ids }).then((r) => r.data),
  revertEnhancement: (mid) => http.delete(`/media/${mid}/enhancement`).then((r) => r.data),
  colorScenes: (id) => http.get(`/projects/${id}/color/scenes`).then((r) => r.data),
  snapshots: (id) => http.get(`/projects/${id}/color/snapshots`).then((r) => r.data),
  saveSnapshot: (id, name) => http.post(`/projects/${id}/color/snapshots`, { name }).then((r) => r.data),
  deleteSnapshot: (id, sid) => http.delete(`/projects/${id}/color/snapshots/${sid}`).then((r) => r.data),
  activateSnapshot: (id, sid) => http.post(`/projects/${id}/color/snapshots/${sid}/activate`).then((r) => r.data),

  // ingest, beta, gpu
  uploadInit: (id, body) => http.post(`/projects/${id}/uploads/init`, body).then((r) => r.data),
  uploadChunk: (uid, i, blob, signal) => http.put(`/uploads/${uid}/chunk/${i}`, blob, { headers: { "Content-Type": "application/octet-stream" }, signal }).then((r) => r.data),
  uploadComplete: (uid) => http.post(`/uploads/${uid}/complete`).then((r) => r.data),
  uploads: (id) => http.get(`/projects/${id}/uploads`).then((r) => r.data),
  importUrls: (id, urls) => http.post(`/projects/${id}/media/import-url`, { urls }).then((r) => r.data),
  importHealth: (id) => http.get(`/projects/${id}/import-health`).then((r) => r.data),
  importHealthRetry: (id) => http.post(`/projects/${id}/import-health/retry`).then((r) => r.data),
  importHealthDismiss: (id) => http.post(`/projects/${id}/import-health/dismiss`).then((r) => r.data),
  approvals: (id) => http.get(`/projects/${id}/approvals`).then((r) => r.data),
  createApproval: (id, body) => http.post(`/projects/${id}/approvals`, body).then((r) => r.data),
  revokeApproval: (id, aid) => http.delete(`/projects/${id}/approvals/${aid}`).then((r) => r.data),
  share: (token, voter_id) => http.get(`/share/${token}`, { params: { voter_id } }).then((r) => r.data),
  shareVote: (token, body) => http.post(`/share/${token}/vote`, body).then((r) => r.data),
  production: (id) => http.get(`/projects/${id}/production`).then((r) => r.data),
  startProduction: (id, body = {}) => http.post(`/projects/${id}/production/start`, body).then((r) => r.data),
  stopProduction: (id) => http.post(`/projects/${id}/production/stop`).then((r) => r.data),
  audioTracks: (id) => http.get(`/projects/${id}/audio-tracks`).then((r) => r.data),
  analyzeTracks: (id) => http.post(`/projects/${id}/audio-tracks/analyze`).then((r) => r.data),
  setCameraTrack: (id, cid, track) => http.put(`/projects/${id}/audio-tracks/camera/${cid}`, { track }).then((r) => r.data),
  setClipTrack: (id, filename, track) => http.put(`/projects/${id}/audio-tracks/clip`, { filename, track }).then((r) => r.data),
  filmReviews: (id) => http.get(`/projects/${id}/film-reviews`).then((r) => r.data),
  createFilmReview: (id, body) => http.post(`/projects/${id}/film-reviews`, body).then((r) => r.data),
  revokeFilmReview: (id, rid) => http.delete(`/projects/${id}/film-reviews/${rid}`).then((r) => r.data),
  resolveFilmComment: (id, rid, cid) => http.post(`/projects/${id}/film-reviews/${rid}/comments/${cid}/resolve`).then((r) => r.data),
  film: (t) => http.get(`/film/${t}`).then((r) => r.data),
  filmComment: (t, body) => http.post(`/film/${t}/comment`, body).then((r) => r.data),
  filmDecision: (t, body) => http.post(`/film/${t}/decision`, body).then((r) => r.data),
  betaReport: (id) => http.get(`/projects/${id}/beta-report`).then((r) => r.data),
  gpu: (refresh = false) => http.get(`/system/gpu?refresh=${refresh}`).then((r) => r.data),
  setEncoderProfile: (body) => http.put(`/system/encoder-profile`, body).then((r) => r.data),
  wizard: (id) => http.get(`/projects/${id}/wizard`).then((r) => r.data),
  wizardRoughCut: (id, target_minutes) => http.post(`/projects/${id}/wizard/rough-cut`, { target_minutes }).then((r) => r.data),
  wizardDeepAI: (id) => http.post(`/projects/${id}/wizard/deep-ai`).then((r) => r.data),
  wizardFinal: (id) => http.post(`/projects/${id}/wizard/final`).then((r) => r.data),
  wizardRetry: (id) => http.post(`/projects/${id}/wizard/retry`).then((r) => r.data),
  wizardScenes: (id) => http.get(`/projects/${id}/wizard/scenes`).then((r) => r.data),
  wizardRemoveScene: (id, sid) => http.delete(`/projects/${id}/wizard/scene/${sid}`).then((r) => r.data),
  wizardRerender: (id) => http.post(`/projects/${id}/wizard/rerender-preview`).then((r) => r.data),
  transferCode: (id) => http.post(`/transfer/${id}/code`, { origin: process.env.REACT_APP_BACKEND_URL }).then((r) => r.data),
  transferImport: (code) => http.post(`/transfer/import`, { code }, { timeout: 180000 }).then((r) => r.data),
  transfersIncoming: () => http.get(`/transfer/incoming`).then((r) => r.data),
  transferRetry: (tid) => http.post(`/transfer/incoming/${tid}/retry`).then((r) => r.data),
  storageStatus: () => http.get(`/system/storage`).then((r) => r.data),
  desktopInfo: () => http.get(`/desktop/info`).then((r) => r.data),
  desktopSettings: () => http.get(`/desktop/settings`).then((r) => r.data),
  saveDesktopSettings: (body) => http.put(`/desktop/settings`, body).then((r) => r.data),
  importFolder: (id, body) => http.post(`/projects/${id}/import-folder`, body, { timeout: 300000 }).then((r) => r.data),
  benchmark: () => http.post(`/system/encode-benchmark`, null, { timeout: 180000 }).then((r) => r.data),
};

export const trackPreviewUrl = (mid, idx) => `${API}/media/${mid}/audio-track/${idx}/preview`;
export const filmVideoUrl = (t) => `${API}/film/${t}/video`;
export const fmtTime = (t) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}`;
export const snapshotPreviewUrl = (mid, sid, v) => `${API}/media/${mid}/color-preview?graded=true&snapshot=${sid}&v=${v}`;

export const colorPreviewUrl = (mid, graded, v) => `${API}/media/${mid}/color-preview?graded=${graded}&v=${v}`;

export const faceImageUrl = (fid) => `${API}/faces/${fid}/image`;
export const sceneVideoUrl = (id, sid, v) => `${API}/projects/${id}/wizard/scene/${sid}/video?v=${v || ""}`;
export const renderAssetUrl = (id, kind) => `${API}/projects/${id}/render/asset/${kind}`;
export const exportAssetUrl = (id, kind) => `${API}/projects/${id}/exports/${kind}/asset`;
export const timelineExportUrl = (id, fmt) => `${API}/projects/${id}/timeline-export?fmt=${fmt}`;

export const fmtBytes = (b) => {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(b) / Math.log(1024));
  return `${(b / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
};

export const fmtDuration = (s) => {
  if (!s && s !== 0) return "--:--";
  s = Math.round(s);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
    : `${m}:${String(sec).padStart(2, "0")}`;
};
